import React, { ReactNode, KeyboardEvent } from 'react';
import cx from 'classnames';

import { TrackStatusIcon, IconErrorCircle } from '../..';

const labelClasses =
    'respond-to-mouse leading-tight clamp-1-line break-words px-1 text-xs';

interface TitleFilenameLabelProps {
    audioDeleted: boolean;
    children?: ReactNode;
    filename: Maybe<string>;
    isNew?: boolean;
    onCancelUpload?(): void;
    title: Maybe<string>;
    underline: boolean;
    uploadingFilename?: string;
    width: number;
}

const newDotSpacer = 16;
const cancelButtonSpacer = 55;
const iconWidth = 20;

/**
 * Displays a label comprised of a title and filename, each within its own truncated line.
 * @param props - {@link TitleFilenameLabelProps}
 */
export const TitleFilenameLabel = ({
    audioDeleted,
    filename,
    isNew = false,
    onCancelUpload,
    title,
    underline,
    uploadingFilename,
    width,
}: TitleFilenameLabelProps): JSX.Element => {
    const baseWidth = width - iconWidth;
    const finalWidth = isNew ? baseWidth - newDotSpacer : baseWidth;
    const renderCancelButton =
        onCancelUpload !== undefined && uploadingFilename !== undefined;
    const displayFilename =
        (renderCancelButton ? uploadingFilename : filename) ?? null;

    return (
        <div className="flex items-center">
            <TrackStatusIcon
                audioDeleted={audioDeleted}
                filename={filename}
                title={title}
                width={iconWidth}
            />
            <div className="flex flex-col items-start justify-center">
                {title && (
                    <div
                        className={cx(labelClasses, 'font-medium text-black', {
                            underline,
                        })}
                        style={{
                            width: finalWidth,
                        }}
                    >
                        {title}
                    </div>
                )}
                {displayFilename && (
                    <div className="flex items-center justify-start">
                        {renderCancelButton && (
                            <div
                                className="relative ml-2"
                                role="button"
                                onKeyPress={(e: KeyboardEvent): void => {
                                    if (e.key === 'Enter') {
                                        if (onCancelUpload) {
                                            onCancelUpload();
                                        }
                                    }
                                }}
                                onClick={(): void => {
                                    if (onCancelUpload) {
                                        onCancelUpload();
                                    }
                                }}
                                tabIndex={0}
                                style={{
                                    width: 13,
                                    height: 13,
                                }}
                            >
                                <IconErrorCircle width={13} fillColor="black" />
                            </div>
                        )}
                        <div
                            className={cx(labelClasses, 'text-tertiary-400', {
                                underline,
                            })}
                            style={{
                                maxWidth: renderCancelButton
                                    ? finalWidth - cancelButtonSpacer
                                    : finalWidth,
                            }}
                        >
                            <div>{displayFilename}</div>
                        </div>

                        {isNew && (
                            <div style={{ width: newDotSpacer }}>
                                <div
                                    className="mr-3 rounded-full bg-secondary-500"
                                    style={{ width: 6, height: 6 }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
